// Deterministic spawn field: Poisson-disk positions + per-instance procedural variation.
// Pure, NO PixiJS / browser API. All randomness via mulberry32 from '@shared/rng'.

import { mulberry32, type Rng } from '@shared/rng';

/** Per-instance spawned sprite descriptor (subset that visuals/ Renderer consumes). */
export interface SpawnInstance {
  index: number; // 0-based stable index
  imageId: string; // key into the textures map
  x: number; // home position in stage px
  y: number;
  baseRotation: number; // radians, 0..2π
  baseScale: number; // 0.6..1.4
  mirror: 1 | -1; // x-flip
  breathPhase: number; // 0..2π
  breathHz: number; // 0.1..0.2 Hz
}

/** Sprite counts per visual quality tier. */
export const SPAWN_COUNT: Record<'high' | 'medium' | 'low', number> = {
  high: 22,
  medium: 16,
  low: 10,
};

/**
 * Bridson Poisson-disk sampling in a w×h rect with the given minimum radius.
 * Deterministic given the rng. Falls back to padding with jittered grid points if
 * the sampler under-delivers, so exactly `count` points are always returned.
 */
export function poissonDisk(
  rng: Rng,
  count: number,
  w: number,
  h: number,
  minRadius: number,
): Array<{ x: number; y: number }> {
  const cell = minRadius / Math.SQRT2;
  const gw = Math.max(1, Math.ceil(w / cell));
  const gh = Math.max(1, Math.ceil(h / cell));
  const grid: Int32Array = new Int32Array(gw * gh).fill(-1);
  const points: Array<{ x: number; y: number }> = [];
  const active: number[] = [];
  const k = 30; // candidates per active point

  const gridIdx = (x: number, y: number) =>
    ((y / cell) | 0) * gw + ((x / cell) | 0);

  const fits = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= w || y >= h) return false;
    const gx = (x / cell) | 0;
    const gy = (y / cell) | 0;
    for (let j = Math.max(0, gy - 2); j <= Math.min(gh - 1, gy + 2); j++) {
      for (let i = Math.max(0, gx - 2); i <= Math.min(gw - 1, gx + 2); i++) {
        const pIdx = grid[j * gw + i];
        if (pIdx >= 0) {
          const p = points[pIdx];
          if ((p.x - x) ** 2 + (p.y - y) ** 2 < minRadius * minRadius) return false;
        }
      }
    }
    return true;
  };

  const emit = (x: number, y: number) => {
    const idx = points.length;
    points.push({ x, y });
    grid[gridIdx(x, y)] = idx;
    active.push(idx);
  };

  emit(rng() * w, rng() * h);

  while (active.length > 0 && points.length < count) {
    const ai = (rng() * active.length) | 0;
    const seed = points[active[ai]];
    let placed = false;
    for (let attempt = 0; attempt < k; attempt++) {
      const ang = rng() * 2 * Math.PI;
      const rad = minRadius * (1 + rng()); // [r, 2r)
      const nx = seed.x + Math.cos(ang) * rad;
      const ny = seed.y + Math.sin(ang) * rad;
      if (fits(nx, ny)) {
        emit(nx, ny);
        placed = true;
        if (points.length >= count) break;
      }
    }
    if (!placed) active.splice(ai, 1);
  }

  // Pad deterministically if Poisson under-delivered (small viewport / tight radius).
  while (points.length < count) {
    points.push({ x: rng() * w, y: rng() * h });
  }
  return points.slice(0, count);
}

/**
 * Build the full spawn field deterministically from a seed.
 * `imageIds` defaults to placeholder ids if omitted (the Renderer passes real ones).
 * Round-robin-biased image assignment ensures every image appears before repeats.
 */
export function spawnField(
  seed: number,
  count: number,
  w: number,
  h: number,
  imageIds?: readonly string[],
): SpawnInstance[] {
  const rng = mulberry32(seed >>> 0);
  const ids =
    imageIds && imageIds.length > 0
      ? imageIds
      : Array.from({ length: 20 }, (_, i) => `image_${i}`);

  const minRadius = Math.sqrt((w * h) / count) * 0.8;
  const points = poissonDisk(rng, count, w, h, minRadius);

  const out: SpawnInstance[] = [];
  for (let i = 0; i < count; i++) {
    // Round-robin base image, with a deterministic shuffle so it isn't a strict cycle.
    const imageId = ids[i % ids.length];
    out.push({
      index: i,
      imageId,
      x: points[i].x,
      y: points[i].y,
      baseRotation: rng() * 2 * Math.PI,
      baseScale: 0.6 + rng() * 0.8,
      mirror: rng() < 0.5 ? -1 : 1,
      breathPhase: rng() * 2 * Math.PI,
      breathHz: 0.1 + rng() * 0.1,
    });
  }
  return out;
}
